import { type Node } from '@xyflow/react';
import {
  AllowedEmailTo, TaskScheduleUnitEnum, WebengageMappingSourceType, WebengageMappingUserField,
} from 'automations/data/actionNodes';
import { z } from 'zod';

import { ActionTypes } from '../automationConfig';
import { CommonNodeDataSchema } from './node';
import { AnchoredTimeSchema } from './timeBuffer';

export const actionNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.nativeEnum(ActionTypes),
});

export const UpdateLeadNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.updateLead),
  additionalOptions: z.object({
    updateLead: z.array(z.object({
      schemaName: z.string({ required_error: 'Lead field is required' }),
      value: z.string({ required_error: 'Value is required' }).min(1, 'Value is required'),
    })).min(1, 'At least one lead field is required')
      .refine((fields) => new Set(fields.map(({ schemaName }) => schemaName)).size === fields.length, {
        message: 'All Lead Fields must be unique',
      }),
  }, {
    required_error: 'At least one lead field is required',
  }),
});

export const WebhookNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.webhook),
  additionalOptions: z.object({
    webhookId: z.union([z.string(), z.number()], {
      required_error: 'Webhook is required',
    }),
  }, {
    required_error: 'Webhook is required',
  }),
});

export const DistributeLeadNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.distributeLead),
  additionalOptions: z.object({
    leadDistributionId: z.number({ required_error: 'Lead Distribution Rule is required' }),
  }, {
    required_error: 'Lead Distribution Rule is required',
  }),
});

export const SendEmailNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.sendEmail),
  additionalOptions: z.object({
    sendEmail: z.object({
      to: z.nativeEnum(AllowedEmailTo, {
        required_error: 'Email To is required',
      }),
      from: z.string({
        required_error: 'Email From is required',
      }).min(1, 'Email From is required'),
      templateId: z.number({
        required_error: 'Email Template is required',
      }),
    }),
  }, {
    required_error: 'Email Content is required',
  }),
});

export const CreateTaskNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.createTask),
  additionalOptions: z.object({
    createTask: z.object({
      templateId: z.number({ required_error: 'Task Template is required' }),
      associateWith: z.literal('lead', { required_error: 'Associate With is required' }),
      owner: z.literal('lead.owner', { required_error: 'Owner is required' }),
      organizer: z.literal('lead.owner', { required_error: 'Organizer is required' }),
      subject: z.string().min(1, 'Subject is required'),
      description: z.string().min(1, 'Description is required'),

      schedule: z.object({
        value: z.number(),
        unit: z.nativeEnum(TaskScheduleUnitEnum),
        isAfter: z.boolean(),
        dependsOn: z.string(),
      }).required(),

      reminder: z.union([
        z.object({
          seconds: z.number(),
        }),
        z.object({
          beforeDays: z.number(),
          atTime: z.string(),
        }),
      ]).optional(),

      config: z.object({
        calendarInviteForLead: z.boolean().optional(),
        calendarInviteForOwner: z.boolean().optional(),
        calendarInviteForOrganizer: z.boolean().optional(),
      }).optional(),
    }),
  }, {
    required_error: 'Task Config is required',
  }),
});

export const CompleteTaskNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.completeTask),
  additionalOptions: z.object({
    completeTask: z.object({
      templateIds: z.array(z.number()).min(1, 'At least one template is required'),
    }),
  }, {
    required_error: 'Task template is required',
  }),
});

export const AddLeadActivityNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.addLeadActivity),
  additionalOptions: z.object({
    addLeadActivity: z.object({
      activityId: z.number({ required_error: 'Activity is required' }),
      activityAttributes: z.array(z.object({
        schemaName: z.string(),
        value: z.string(),
      })).optional(),
    }),
  }, {
    required_error: 'Activity is required',
  }),
});

export const SendWebengageEventNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.sendWebengageEvent),
  additionalOptions: z.object({
    sendWebengageEvent: z.object({
      eventName: z.string({ required_error: 'Event Name is required' }).min(1, 'Event Name is required'),
      mappings: z.array(z.object({
        webengageAttribute: z.string({ required_error: 'WebEngage Attribute is required' })
          .min(1, 'WebEngage Attribute is required'),
        sourceType: z.nativeEnum(WebengageMappingSourceType, { required_error: 'Source Type is required' }),
        sourceAttribute: z.string().optional(),
        sourceValue: z.string().optional(),
        userField: z.nativeEnum(WebengageMappingUserField).optional(),
      }).superRefine((mapping, ctx) => {
        // A static source needs a hardcoded value; an attribute source needs a schema name.
        if (mapping.sourceType === WebengageMappingSourceType.STATIC) {
          if (!mapping.sourceValue) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sourceValue'], message: 'Value is required' });
          }
        } else if (!mapping.sourceAttribute) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom, path: ['sourceAttribute'], message: 'Source Attribute is required',
          });
        }
      })).min(1, 'At least one mapping is required')
        .refine(
          (mappings) => new Set(mappings.map(({ webengageAttribute }) => webengageAttribute)).size === mappings.length,
          { message: 'All WebEngage Attributes must be unique' },
        ),
    }),
  }, {
    required_error: 'WebEngage Event Config is required',
  }),
});

export const TriggerAiCallNodeDataSchema = actionNodeDataSchema.extend({
  nodeSubType: z.literal(ActionTypes.triggerAiCall),
  additionalOptions: z.object({
    triggerAiCall: z.object({
      agentUuid: z.string({ required_error: 'Agent is required' }),
      skipCriteriaGroupId: z.number().optional(),
      maxRetries: z.number().int().min(0).optional(),
      retryGap: z.object({
        value: z.number().int().min(1),
        unit: z.enum(['minutes', 'hours']),
      }).optional(),
      schedule: AnchoredTimeSchema.optional(),
      callingWindow: z.object({
        days: z.array(z.number().int().min(0).max(6)).min(1),
        startTime: z.string(),
        endTime: z.string(),
      }).optional(),
      // When true, the BE only places the call if the lead's owner has the ai_call_lead_trigger
      // feature; otherwise the dispatch is blocked. Kept in the schema (not only the form) so the
      // canvas round-trips it on save instead of silently stripping it.
      requireOwnerLeadTrigger: z.boolean().optional(),
    }, {
      required_error: 'Agent is required',
    })
      .superRefine((val, ctx) => {
        if ((val.maxRetries ?? 0) > 0 && !val.retryGap) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['retryGap'],
            message: 'Retry gap is required when max retries is set',
          });
        }
      }),
  }, {
    required_error: 'Agent is required',
  }),
});

export type ActionNodeData = z.infer<typeof actionNodeDataSchema>;
export type UpdateLeadNodeData = z.infer<typeof UpdateLeadNodeDataSchema>;
export type WebhookNodeData = z.infer<typeof WebhookNodeDataSchema>;
export type DistributeLeadNodeData = z.infer<typeof DistributeLeadNodeDataSchema>;
export type SendEmailNodeData = z.infer<typeof SendEmailNodeDataSchema>;
export type CreateTaskNodeData = z.infer<typeof CreateTaskNodeDataSchema>;
export type AddLeadActivityNodeData = z.infer<typeof AddLeadActivityNodeDataSchema>;
export type CompleteTaskNodeData = z.infer<typeof CompleteTaskNodeDataSchema>;
export type SendWebengageEventNodeData = z.infer<typeof SendWebengageEventNodeDataSchema>;
export type TriggerAiCallNodeData = z.infer<typeof TriggerAiCallNodeDataSchema>;

export type ActionNodeType = Node<ActionNodeData>;
export type UpdateLeadNodeType = Node<UpdateLeadNodeData>;
export type WebhookNodeType = Node<WebhookNodeData>;
export type DistributeLeadNodeType = Node<DistributeLeadNodeData>;
export type SendEmailNodeType = Node<SendEmailNodeData>;
export type CreateTaskNodeType = Node<CreateTaskNodeData>;
export type CompleteTaskNodeType = Node<CompleteTaskNodeData>;
export type AddLeadActivityNodeType = Node<AddLeadActivityNodeData>;
export type SendWebengageEventNodeType = Node<SendWebengageEventNodeData>;
export type TriggerAiCallNodeType = Node<TriggerAiCallNodeData>;
